import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type FormEvent } from "react";
import { FileDropZone } from "../FileDropZone";
import { useTranslation } from "react-i18next";
import {
  attachmentsApi,
  isImageContentType,
  isModelAttachment,
  LAYOUT_BACKGROUND_KIND,
  type AttachmentView,
} from "../../api/attachments";
import { layoutsApi, type LayoutDetail, type LayoutKind } from "../../api/layouts";
import type { PositionNode } from "../../api/structure";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { useAttachmentUpload } from "../../lib/useAttachmentUpload";
import { Modal } from "../Modal";
import { PositionTreePicker } from "../PositionTreePicker";
import { UploadRows, attachmentKindLabel } from "../UploadRows";
import Ic from "../icons.jsx";

type Translate = (key: string, fallback: string, opts?: Record<string, unknown>) => string;

export type LayoutPositionRef = { id: string; name: string; code: string | null; sequenceNo?: number | null };


function positionTitle(p: LayoutPositionRef): string {
  return p.sequenceNo !== null && p.sequenceNo !== undefined ? `${p.name} #${p.sequenceNo}` : p.name;
}

export function translateLayoutError(t: Translate, ae: ApiError): string {
  const d = detailsMap(ae);
  if (ae.code === "VALIDATION" && d?.attachmentId) {
    switch (d.attachmentId) {
      case "NOT_FOUND": return t("layout.error.attachmentMissing", "Prilog više ne postoji.");
      case "UNSUPPORTED_TYPE": return t("layout.error.unsupportedType", "Podloga mora biti slika (JPEG, PNG, WebP) ili 3D model (GLB).");
      case "KIND_MISMATCH": return t("layout.error.kindMismatch", "Podloga mora ostati iste vrste: slika za 2D, 3D model za 3D layout.");
      case "WRONG_ENTITY": return t("layout.error.wrongEntity", "Prilog ne pripada ovoj poziciji.");
      case "NOT_READY": return t("layout.error.notReady", "Prilog još nije potvrđen.");
      default:
    }
  }
  if (ae.code === "MODEL_INVALID") {
    const reason = d?.reason ?? "";
    if (reason === "UNSUPPORTED_EXTENSION") return t("layout.error.modelExtension", "Model koristi {{ext}} — izvezi ga bez te kompresije ili sa meshopt.", { ext: d?.extension ?? "?" });
    if (reason === "NO_PARTS") return t("layout.error.modelNoParts", "Model nema nijedan deo koji se crta.");
    return t("layout.error.modelInvalid", "3D model se ne može pročitati ({{reason}}).", { reason });
  }
  if (ae.code === "VALIDATION" && d?.shape === "WRONG_KIND") return t("layout.error.wrongKind", "Takav marker ne ide na ovu vrstu layouta.");
  if (ae.code === "VALIDATION" && d?.parts === "UNKNOWN_PART") return t("layout.error.unknownPart", "Deo ne postoji u modelu.");
  if (ae.code === "VALIDATION" && d?.positionId) {
    switch (d.positionId) {
      case "NOT_DESCENDANT": return t("layout.error.notDescendant", "Pozicija nije u grani ovog layouta.");
      case "ALREADY_PLACED": return t("layout.error.alreadyPlaced", "Pozicija je već na ovom layoutu.");
      case "NOT_FOUND": return t("layout.error.positionMissing", "Pozicija više ne postoji.");
      default:
    }
  }
  if (ae.code === "VALIDATION" && d?.points) {
    switch (d.points) {
      case "TOO_FEW": return t("layout.error.pointsTooFew", "Oblik mora imati bar 3 tačke.");
      case "TOO_MANY": return t("layout.error.pointsTooMany", "Oblik može imati najviše 200 tačaka.");
      case "DEGENERATE": return t("layout.error.pointsDegenerate", "Sve tačke oblika su na jednoj pravoj.");
      case "OUT_OF_RANGE": return t("layout.error.pointsOutOfRange", "Tačka oblika je van slike.");
      case "MALFORMED": return t("layout.error.pointsMalformed", "Tačke oblika nisu ispravno zadate.");
      default:
    }
  }
  if (ae.code === "UPLOAD_INVALID") return t("layout.error.uploadInvalid", "Slika se ne može pročitati.");
  return formatApiError(ae);
}

const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";
const MODEL_ACCEPT = ".glb,model/gltf-binary";

/**
 * Picks the background of a layout: the position's existing pictures and 3D
 * models (contract §5, §9) or a fresh upload through the shared presign → PUT →
 * confirm hook. `only` narrows the list to one kind — a replaced background must
 * stay of the layout's kind.
 */
export function BackgroundPicker({ positionId, value, onChange, only }: {
  positionId: string;
  value: AttachmentView | null;
  onChange: (a: AttachmentView | null) => void;
  only?: LayoutKind;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<AttachmentView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const kind = LAYOUT_BACKGROUND_KIND;
  const fileRef = useRef<HTMLInputElement>(null);
  const accept = only === "IMAGE" ? IMAGE_ACCEPT : only === "MODEL" ? MODEL_ACCEPT : `${IMAGE_ACCEPT},${MODEL_ACCEPT}`;

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    attachmentsApi.list({ entityKind: "POSITION", entityId: positionId })
      .then((list) => {
        if (cancelled) return;
        // Backgrounds first, then any other picture or model of the position (a photo can serve as a background too).
        const fits = (a: AttachmentView) => (only === "MODEL" ? isModelAttachment(a) : only === "IMAGE" ? isImageContentType(a.contentType) : isImageContentType(a.contentType) || isModelAttachment(a));
        const pics = list.filter((a) => a.status === "READY" && fits(a));
        setItems([...pics.filter((a) => a.kind === "LAYOUT"), ...pics.filter((a) => a.kind !== "LAYOUT")]);
      })
      .catch((err: ApiError) => { if (!cancelled) { setError(err.message); setItems([]); } });
    return () => { cancelled = true; };
  }, [positionId, only]);

  const onUploaded = useCallback((view: AttachmentView) => {
    setItems((prev) => [view, ...(prev ?? [])]);
    onChange(view);
  }, [onChange]);
  const upload = useAttachmentUpload("POSITION", positionId, onUploaded);

  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    void upload.addFiles(files.slice(0, 1), kind);
  }

  return (
    <FileDropZone onFiles={(files) => { void upload.addFiles(files, kind); }} accept={accept} multiple={false}>
    <div className="layout-bg-picker">
      {error && <div style={errorBox} role="alert">{error}</div>}
      {items === null ? (
        <div className="empty">{t("common.loading", "Učitavanje…")}</div>
      ) : items.length === 0 ? (
        <div className="empty layout-bg-empty">
          {only === "MODEL"
            ? t("layout.bg.noneModel", "Pozicija nema drugi 3D model — priložite GLB.")
            : only === "IMAGE"
              ? t("layout.bg.noneImage", "Pozicija još nema nijednu sliku — priložite fotografiju ili crtež.")
              : t("layout.bg.none", "Pozicija još nema nijednu sliku ni 3D model — priložite fotografiju, crtež ili GLB.")}
        </div>
      ) : (
        <div className="layout-bg-grid" role="radiogroup" aria-label={t("layout.bg.pick", "Izbor podloge")}>
          {items.map((a) => {
            const active = value?.id === a.id;
            return (
              <button
                key={a.id}
                type="button"
                role="radio"
                aria-checked={active}
                className={`layout-bg-option${active ? " is-active" : ""}`}
                onClick={() => onChange(active ? null : a)}
                title={a.filename}
              >
                {isModelAttachment(a)
                  ? <span className="layout-bg-thumb is-model" title={t("layout.kind.model", "3D model")}><Ic.box aria-hidden="true" /></span>
                  : <span className="layout-bg-thumb"><img src={`/api/attachments/${a.id}/thumbnail`} alt="" loading="lazy" /></span>}
                <span className="layout-bg-name">{a.filename}</span>
                <span className="layout-bg-meta muted">{attachmentKindLabel(t, a.kind)} · {a.code}</span>
                {active && <span className="layout-bg-check"><Ic.check /></span>}
              </button>
            );
          })}
        </div>
      )}
      <div className="att-upload">
        <input ref={fileRef} type="file" accept={accept} hidden aria-label={uploadLabel(t, only)} onChange={onPick} />
        <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
          <Ic.upload /> {uploadLabel(t, only)}
        </button>
        <span className="att-hint muted">
          {only === "MODEL"
            ? t("layout.bg.hintModel", "GLB (3D model) — ili prevucite fajl ovde. Markeri zadržavaju delove koje novi model i dalje ima.")
            : only === "IMAGE"
              ? t("layout.bg.hintImage", "JPEG, PNG ili WebP — ili prevucite sliku ovde. Podloga je zasebna kategorija i ne vidi se među prilozima.")
              : t("layout.bg.hint", "JPEG, PNG, WebP ili GLB (3D model) — ili prevucite fajl ovde. Podloga je zasebna kategorija i ne vidi se među prilozima.")}
        </span>
      </div>
      <UploadRows uploads={upload.uploads} onRetry={(u) => { void upload.retry(u); }} onDismiss={upload.dismiss} />
    </div>
    </FileDropZone>
  );
}

function uploadLabel(t: Translate, only?: LayoutKind): string {
  if (only === "MODEL") return t("layout.bg.uploadModel", "Priloži 3D model");
  if (only === "IMAGE") return t("layout.bg.uploadImage", "Priloži novu sliku");
  return t("layout.bg.upload", "Priloži sliku ili 3D model");
}

/**
 * "Novi layout" (contract §5): step 1 picks the position in the tree (skipped
 * when the caller already knows it), step 2 the name and the background — a
 * picture (2D) or a GLB model (3D, §9); the server tells them apart.
 */
export function NewLayoutModal({ position, onClose, onCreated }: {
  /** Preselected position (structure card); otherwise the tree picker is shown first. */
  position?: LayoutPositionRef | null;
  onClose: () => void;
  onCreated: (detail: LayoutDetail) => void;
}) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<LayoutPositionRef | null>(position ?? null);
  const [step, setStep] = useState<1 | 2>(position ? 2 : 1);
  const [name, setName] = useState("");
  const [background, setBackground] = useState<AttachmentView | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const suggestedName = useMemo(() => (picked ? positionTitle(picked) : ""), [picked]);

  function onTreePick(n: PositionNode) {
    setPicked({ id: n.id, name: n.name, code: n.code, sequenceNo: n.sequenceNo });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!picked || !background) return;
    setError(null);
    setSubmitting(true);
    try {
      onCreated(await layoutsApi.create(picked.id, { name: name.trim() || suggestedName, attachmentId: background.id }));
    } catch (err) {
      setError(translateLayoutError(t, err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={t("layout.new.title", "Novi layout")}
      subtitle={step === 1
        ? t("layout.new.subPick", "Korak 1 od 2 — izaberite poziciju (pogon, liniju, mašinu ili sklop) čiju sliku raspoređujete.")
        : t("layout.new.subBackground", "Korak 2 od 2 — {{name}}: naziv layouta i podloga.", { name: picked ? positionTitle(picked) : "" })}
      onClose={onClose}
      width={680}
    >
      {step === 1 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <PositionTreePicker value={picked?.id ?? null} onChange={onTreePick} />
          <div className="muted" style={{ fontSize: 12.5 }}>
            {picked
              ? t("layout.new.picked", "Izabrano: {{name}}", { name: positionTitle(picked) })
              : t("structure.modal.noTarget", "Nije izabran cilj.")}
          </div>
          <div className="form-actions">
            <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
            <button type="button" className="btn primary" disabled={!picked} onClick={() => setStep(2)}>
              {t("action.next", "Dalje")} <Ic.arrowRight />
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label className="field">
            <span>{t("layout.field.name", "Naziv layouta")}</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder={suggestedName} />
          </label>
          <div className="field">
            <span>{t("layout.field.background", "Podloga")} <span className="req-mark">*</span></span>
            {picked && <BackgroundPicker positionId={picked.id} value={background} onChange={setBackground} />}
          </div>
          {error && <div style={errorBox} role="alert">{error}</div>}
          <div className="form-actions">
            {!position && (
              <button type="button" className="btn" style={{ marginRight: "auto" }} onClick={() => setStep(1)}>
                {t("action.back", "Nazad")}
              </button>
            )}
            <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
            <button type="submit" className="btn primary" disabled={submitting || !background}>
              {submitting ? t("action.saving", "Čuvanje…") : t("layout.new.create", "Napravi layout")}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** "Zameni podlogu" — same picker; markers keep their normalised coordinates (contract §3). */
export function ReplaceBackgroundModal({ layout, onClose, onSaved }: {
  layout: LayoutDetail;
  onClose: () => void;
  onSaved: (detail: LayoutDetail) => void;
}) {
  const { t } = useTranslation();
  const [background, setBackground] = useState<AttachmentView | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!background) return;
    setError(null);
    setSubmitting(true);
    try {
      onSaved(await layoutsApi.update(layout.id, { name: layout.name, sortOrder: layout.sortOrder, attachmentId: background.id }));
    } catch (err) {
      setError(translateLayoutError(t, err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={t("layout.replaceBg.title", "Zamena podloge")}
      subtitle={layout.kind === "MODEL"
        ? t("layout.replaceBg.subModel", "Markeri zadržavaju delove koje novi model i dalje ima — proverite ih posle zamene.")
        : t("layout.replaceBg.sub", "Markeri ostaju na istim relativnim mestima — proverite ih posle zamene.")}
      onClose={onClose}
      width={680}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <BackgroundPicker positionId={layout.positionId} value={background} onChange={setBackground} only={layout.kind} />
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting || !background || background.id === layout.attachmentId}>
            {submitting ? t("action.saving", "Čuvanje…") : t("layout.replaceBg.confirm", "Zameni podlogu")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
